import { Box, type SxProps, type Theme } from "@mui/material";
import { type FC, useMemo } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { FONT_SIZE, INK, NEUTRAL } from "../../../theme/tokens";
import { MetaChip } from "../Badges/MetaChip";
import { HorizontalScrollArea } from "../Tables/HorizontalScrollArea";
import { CHAT_BADGE_TAG, remarkChatBadges } from "./chatBadges";

type Props = {
	/** The markdown source to render (GFM: lists, bold, tables, task lists, links). */
	children: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * Root style rhythm for a block of rendered markdown inside a chat surface:
 * collapses the first/last child margins so the container's own padding owns the
 * outer spacing, and sets a compact vertical rhythm between blocks. All colors /
 * borders come from theme tokens so a markdown answer matches the app.
 */
const ROOT_SX: SxProps<Theme> = {
	fontSize: FONT_SIZE.EMPHASIS,
	lineHeight: 1.55,
	color: INK.PRIMARY,
	overflowWrap: "break-word",
	// Block rhythm — every direct block gets a top gap except the first.
	"& > *": { m: 0 },
	"& > * + *": { mt: 1 },
	// Headings: step down the type scale; the answer is prose, not a page.
	"& h1, & h2, & h3, & h4, & h5, & h6": { m: 0, fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, lineHeight: 1.3 },
	"& h1 + *, & h2 + *, & h3 + *, & h4 + *, & h5 + *, & h6 + *": { mt: 0.5 },
	// Lists: hanging indent, tight item spacing.
	"& ul, & ol": { m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.25 },
	"& li": { m: 0 },
	"& li > ul, & li > ol": { mt: 0.25 },
	"& strong": { fontWeight: 700 },
	"& em": { fontStyle: "italic" },
	"& a": { color: "primary.main", textDecoration: "underline", overflowWrap: "anywhere" },
	"& hr": { border: 0, borderTop: `1px solid ${NEUTRAL[200]}`, my: 1 },
	"& blockquote": {
		m: 0,
		pl: 1.25,
		borderLeft: `3px solid ${NEUTRAL[300]}`,
		color: INK.MUTED,
	},
	// Inline code chip.
	"& code": {
		fontFamily: "monospace",
		fontSize: "0.85em",
		backgroundColor: NEUTRAL[100],
		borderRadius: 0.5,
		px: 0.5,
		py: 0.125,
	},
	// Fenced code block — scrolls rather than stretching the surface.
	"& pre": {
		m: 0,
		p: 1.25,
		backgroundColor: NEUTRAL[100],
		borderRadius: 1,
		overflowX: "auto",
	},
	"& pre code": { backgroundColor: "transparent", p: 0 },
};

// GFM tables can be wide (a roster / briefing); wrap so they scroll inside the
// surface instead of forcing it wider. The wrapper carries its OWN white surface
// (not the container's) so the table reads on any bubble/card — the assistant
// bubble is NEUTRAL[150], nearly the old NEUTRAL[100] header fill, so the header
// used to vanish. On the white surface the NEUTRAL[200] header band has real
// contrast. The wrapper also paints a pure-CSS horizontal scroll affordance
// (Lea Verou `background-attachment: local, scroll`): white "cover" layers scroll
// WITH the content (attachment: local) and mask the edge "shadow" layers (pinned
// to the viewport edges, attachment: scroll) whenever the content reaches that
// edge — so the fade appears only while there is more to scroll and disappears at
// the ends / when the table fits. No JS, no scroll listeners. Body cells are
// transparent so the surface + shadows show through; only the header is filled.
const TABLE_SX: SxProps<Theme> = {
	"& table": {
		borderCollapse: "collapse",
		width: "max-content",
		minWidth: "100%",
		fontSize: FONT_SIZE.SECONDARY,
		backgroundColor: "transparent",
	},
	"& th, & td": {
		textAlign: "left",
		px: 1,
		py: 0.75,
		borderBottom: `1px solid ${NEUTRAL[200]}`,
		whiteSpace: "nowrap",
	},
	"& td": { backgroundColor: "transparent" },
	"& th": { backgroundColor: NEUTRAL[200], fontWeight: 700, color: INK.SECONDARY },
	"& tr:last-of-type td": { borderBottom: 0 },
};

/**
 * Renders a streamed/stored markdown answer for any chat surface (Annie read
 * answers, guide replies) as themed HTML: lists, emphasis, links, code, and GFM
 * tables. Generic and prop-driven — the caller owns the container (a bubble /
 * card); this owns only the markdown-to-themed-elements mapping. Links open in a
 * new tab safely. All colors/spacing are theme tokens (Rule 5).
 *
 * Adds one convention on top of GFM: `[[Label]]` renders as a `MetaChip` badge,
 * so an answer can flag a row ("[[Replace]]") with a real chip rather than
 * punctuation. See `chatBadges`.
 */
export const ChatMarkdown: FC<Props> = ({ children, dataTestId, sx }) => {
	// Tables get a scroll wrapper; everything else falls through to the tokenized
	// element styles on ROOT_SX. Anchor targets are hardened to a new tab.
	const components = useMemo<Components>(
		() => ({
			table: ({ children: tableChildren }) => (
				<HorizontalScrollArea sx={TABLE_SX}>
					<table>{tableChildren}</table>
				</HorizontalScrollArea>
			),
			// `[[Label]]` → the app's generic annotation chip (see chatBadges). The plugin only ever gives
			// it a single text child, so anything else means a hand-written <mark> and stays plain text.
			[CHAT_BADGE_TAG]: ({ children: badgeChildren }) =>
				typeof badgeChildren === "string" ? (
					<MetaChip label={badgeChildren} tone="info" dataTestId="chat-badge" sx={{ verticalAlign: "baseline" }} />
				) : (
					badgeChildren
				),
			a: ({ children: linkChildren, href }) => (
				<a href={href} target="_blank" rel="noopener noreferrer">
					{linkChildren}
				</a>
			),
		}),
		[],
	);

	return (
		<Box data-testid={dataTestId} sx={[ROOT_SX, ...(Array.isArray(sx) ? sx : [sx])]}>
			<Markdown remarkPlugins={[remarkGfm, remarkChatBadges]} components={components}>
				{children}
			</Markdown>
		</Box>
	);
};
